import { Clock, Loader2 } from "lucide-react"

import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

import {
  blockerFor,
  canAnswerUnreadPages,
  canResubmitCorrectedFile,
  canRunNow,
} from "./blocker-state"
import { StoppedNotice, UnreadPagesPrompt } from "./review-blocker-prompts"

import type { UnreadPagesDecision } from "./api/mutations"
import type { Review } from "./data/review-data"
import type { Role } from "@/lib/roles"

/**
 * Why this review is not moving, above the work it is blocking.
 *
 * Without it a parked review is indistinguishable from a finished one: the
 * findings rail shows whatever the run produced before it stopped, which on a
 * review that compared nothing reads as a clean label. Saying a label passed
 * when nothing was checked is the one thing this screen must never do.
 *
 * Every state here is a wait, and they are not the same wait. The question
 * clears only when somebody answers it, so it gets the room; the overnight lane
 * clears by itself, so it reports rather than asks.
 */
export interface ReviewBlockerStripProps {
  review: Review
  role: Role | undefined
  onAnswerUnreadPages: (decision: UnreadPagesDecision) => void
  answerPending: boolean
  /** Which answer is in flight, so only that button shows a spinner. */
  answerInFlight: UnreadPagesDecision | null
  /** The server's refusal, when there is one. */
  answerError: unknown
  onRunNow: () => void
  runNowPending: boolean
  /**
   * File the corrected label as the next version of this thread.
   *
   * No wizard: the server inherits the masters from the review being replaced,
   * so the file is the only thing left to ask for. Sending the reviewer back
   * through intake would make them re-pick a master they already chose.
   */
  onResubmit: (file: File) => void
  resubmitPending: boolean
  resubmitError: unknown
  className?: string
}

export function ReviewBlockerStrip({
  review,
  role,
  onAnswerUnreadPages,
  answerPending,
  answerInFlight,
  answerError,
  onRunNow,
  runNowPending,
  onResubmit,
  resubmitPending,
  resubmitError,
  className,
}: ReviewBlockerStripProps): JSX.Element | null {
  const blocker = blockerFor(review)
  if (blocker === null) return null

  return (
    <div className={cn("shrink-0 px-6 pt-3", className)}>
      {blocker.kind === "trashed" && (
        <Alert tone="neutral" size="sm" title="This product is in the trash">
          Hidden from everyone but admins. Restore it from the Clients screen to work on it again.
        </Alert>
      )}

      {blocker.kind === "needs_input" && (
        <UnreadPagesPrompt
          review={review}
          canAnswer={canAnswerUnreadPages(role)}
          onAnswer={onAnswerUnreadPages}
          pending={answerPending}
          inFlight={answerInFlight}
          error={answerError}
        />
      )}

      {blocker.kind === "stopped" && (
        <StoppedNotice
          review={review}
          canResubmit={canResubmitCorrectedFile(role)}
          onResubmit={onResubmit}
          pending={resubmitPending}
          error={resubmitError}
        />
      )}

      {blocker.kind === "batch_wait" && (
        <Alert
          tone="warning"
          size="sm"
          title="Running overnight"
          action={
            /* Withheld rather than disabled: a dead button is worse than none,
               and the facts above are for everyone who can read the review. */
            canRunNow(role) ? (
              <Button
                size="sm"
                variant="default"
                leadingIcon={runNowPending ? <Loader2 className="animate-spin" /> : <Clock />}
                disabled={runNowPending}
                onClick={onRunNow}
              >
                Run now instead
              </Button>
            ) : undefined
          }
        >
          {formatExpected(review.batchExpectedBy)}
        </Alert>
      )}
    </div>
  )
}

/**
 * Due by a stated time, or an honest admission that it is not known yet.
 *
 * The zone is named rather than left to the reader's assumption: "due by 9am"
 * means nothing to a reviewer reading it from another country.
 */
function formatExpected(iso: string | null): string {
  if (iso === null) return "The questions are queued with the model. Results arrive within a day."
  const due = new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  })
  return `About half the cost, answered by ${due} and usually sooner.`
}
